import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faPlus,
  faEnvelope,
  faAddressCard,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { useDispatch, useSelector } from "react-redux";
import { setAttendees } from "../../../store/slices/eventSlice";
import { useAxiosInstance } from "../../../services/Interceptor";
import { GetCashToken } from "../../../services/PaymentService";
import { getAuditList } from "../../../services/CombineService";
import AthleteContactModal from "../../../components/contactinfo";
import SendEmailModal from "../../../components/SendEmailModal";
import AttendeesPaymentMethodDialog from "./AttendeesPaymentMethodDialog";
import AttendeesQrCodeDialog from "./AttendeesQrCodeDialog";
import AttendeesSearchList from "./AttendeesSearchList";

export default function Attendees({
  attendees,
  eventId,
  eventName,
  handleAttendeesClick,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const dispatch = useDispatch();
  const selectedAttendee = useSelector((state) => state.events.attendees);
  const [attendeesList, setAttendeesList] = useState([]);

  const [height, setHeight] = useState(window.innerHeight);
  const adjustedHeight = height - 395;
  const apiInstance = useAxiosInstance();
  const [open, setOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [isDownloadingAudit, setIsDownloadingAudit] = useState(false);

  useEffect(() => {
    if (attendees) {
      setAttendeesList(attendees);
    }
  }, [attendees]);

  const handleSearch = (e) => {
    const results = attendees.filter(
      (item) =>
        item.name.toLowerCase().includes(e.target.value.toLowerCase()) ||
        item.lastname.toLowerCase().includes(e.target.value.toLowerCase()) ||
        item.position.toLowerCase().includes(e.target.value.toLowerCase()) ||
        (item.jerseyNumber &&
          item.jerseyNumber.toString().includes(e.target.value)),
    );
    setAttendeesList(results);
  };

  useEffect(() => {
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const updateHeight = () => {
    setHeight(window.innerHeight);
  };

  const handlePaymentMethodSelect = async (method) => {
    try {
      let registrationUrl = `${process.env.REACT_APP_QR_CODE_ATHLETE_REGISTER_URL}${eventId}`;

      if (method === "cash") {
        const response = await GetCashToken(apiInstance);
        const cashToken = response.data.data;
        registrationUrl += `?cashToken=${cashToken}`;
      }

      setQrCodeUrl(registrationUrl);
      setIsOpen(true);
    } catch (error) {
      console.error("Error generating cash token:", error);
    } finally {
      setIsPaymentModalOpen(false);
    }
  };

  const AUDIT_HEADERS = [
    "First Name",
    "Last Name",
    "Jersey #",
    "Profile Picture",
    "Height",
    "Height Video",
    "Weight",
    "Wingspan",
    "Wingspan Video",
    "Overhead Squat Video",
    "Hand",
    "Foot",
    "40 Yard - Att 1 (10yd)",
    "40 Yard Video - Att 1 (10yd)",
    "40 Yard - Att 1 (20yd)",
    "40 Yard - Att 1 (40yd)",
    "40 Yard Video - Att 1 (40yd)",
    "40 Yard - Att 2 (10yd)",
    "40 Yard Video - Att 2 (10yd)",
    "40 Yard - Att 2 (20yd)",
    "40 Yard - Att 2 (40yd)",
    "40 Yard Video - Att 2 (40yd)",
    "Bench Press",
    "Bench Press Video",
    "Vertical Jump",
    "Vertical Jump Video",
    "Broad Jump - Att 1",
    "Broad Jump Video - Att 1",
    "Broad Jump - Att 2",
    "Broad Jump Video - Att 2",
    "20-Yd Shuttle - Att 1",
    "20-Yd Shuttle Video - Att 1",
    "20-Yd Shuttle - Att 2",
    "20-Yd Shuttle Video - Att 2",
    "Three-Cone - Att 1",
    "Three-Cone Video - Att 1",
    "Three-Cone - Att 2",
    "Three-Cone Video - Att 2",
  ];

  const toYesNo = (val) => (val ? "Yes" : "No");

  const auditAthleteToRow = (athlete) => {
    const m = athlete.measurements ?? {};
    const cr = athlete.combineResults ?? {};
    const mv = athlete.measurementVideos ?? {};
    const cv = athlete.combineVideos ?? {};
    return [
      athlete.firstName,
      athlete.lastName,
      athlete.jerseyNumber ?? "",
      toYesNo(athlete.hasProfilePicture),
      toYesNo(m.height),
      toYesNo(mv.height),
      toYesNo(m.weight),
      toYesNo(m.wingspan),
      toYesNo(mv.wingspan),
      toYesNo(mv.overheadSquat),
      toYesNo(m.hand),
      toYesNo(m.foot),
      toYesNo(cr.fortyYardDash_Attempt1_10yd),
      toYesNo(cv.fortyYardDash_Attempt1_10yd),
      toYesNo(cr.fortyYardDash_Attempt1_20yd),
      toYesNo(cr.fortyYardDash_Attempt1_40yd),
      toYesNo(cv.fortyYardDash_Attempt1_40yd),
      toYesNo(cr.fortyYardDash_Attempt2_10yd),
      toYesNo(cv.fortyYardDash_Attempt2_10yd),
      toYesNo(cr.fortyYardDash_Attempt2_20yd),
      toYesNo(cr.fortyYardDash_Attempt2_40yd),
      toYesNo(cv.fortyYardDash_Attempt2_40yd),
      toYesNo(cr.benchPress),
      toYesNo(cv.benchPress),
      toYesNo(cr.verticalJump),
      toYesNo(cv.verticalJump),
      toYesNo(cr.broadJumpAttempt1),
      toYesNo(cv.broadJumpAttempt1),
      toYesNo(cr.broadJumpAttempt2),
      toYesNo(cv.broadJumpAttempt2),
      toYesNo(cr.twentyYardShuttleAttempt1),
      toYesNo(cv.twentyYardShuttleAttempt1),
      toYesNo(cr.twentyYardShuttleAttempt2),
      toYesNo(cv.twentyYardShuttleAttempt2),
      toYesNo(cr.threeConeAttempt1),
      toYesNo(cv.threeConeAttempt1),
      toYesNo(cr.threeConeAttempt2),
      toYesNo(cv.threeConeAttempt2),
    ];
  };

  const escapeCsvValue = (value) => {
    const normalized = value === null || value === undefined ? "" : value;
    return `"${String(normalized).replace(/"/g, '""')}"`;
  };

  const handleDownloadAuditCsv = async () => {
    if (!eventId || isDownloadingAudit) return;
    setIsDownloadingAudit(true);
    try {
      const data = await getAuditList(apiInstance, eventId);
      const rows = data.map(auditAthleteToRow);
      const csvContent = [AUDIT_HEADERS, ...rows]
        .map((row) => row.map(escapeCsvValue).join(","))
        .join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const safeEventName = (eventName ?? "event").replace(
        /[/\\:*?"<>|]/g,
        "_",
      );
      link.setAttribute("download", `${safeEventName}-audit.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (error) {
      console.error("Error downloading audit CSV:", error);
      toast.error("Failed to download audit CSV. Please try again.");
    } finally {
      setIsDownloadingAudit(false);
    }
  };

  const handleSelectAttendee = (athlete) => {
    dispatch(
      setAttendees({
        eventId: eventId,
        userAthlete: athlete,
      }),
    );
    handleAttendeesClick(true);
  };

  return (
    <>
      <div className="mb-0 mt-0 px-4 py-4 pt-0">
        <div className="flex justify-between items-center pb-3">
          <h1 className="uppercase text-xl">Athletes</h1>

          {eventId !== undefined && (
            <div className="flex items-center gap-6">
              <Menu as="div" className="relative inline-block text-left">
                <MenuButton className="inline-flex items-center justify-center gap-2 rounded-md border bg-gray-100 px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 hover:text-gray-700">
                  Actions
                  <FontAwesomeIcon icon={faChevronDown} />
                </MenuButton>
                <MenuItems
                  transition
                  className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                >
                  <div className="py-1">
                    <MenuItem>
                      <button
                        onClick={() => setOpen(true)}
                        className="flex w-full items-center px-4 py-3 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                        type="button"
                      >
                        <FontAwesomeIcon icon={faAddressCard} />
                        <span className="pl-2">Contact Info</span>
                      </button>
                    </MenuItem>
                    <MenuItem>
                      <button
                        className="flex w-full items-center px-4 py-3 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                        onClick={() => setEmailOpen(true)}
                        type="button"
                      >
                        <FontAwesomeIcon icon={faEnvelope} />
                        <span className="pl-2">Send Email</span>
                      </button>
                    </MenuItem>
                    <MenuItem disabled={isDownloadingAudit}>
                      <button
                        className="flex w-full items-center px-4 py-3 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
                        onClick={handleDownloadAuditCsv}
                        type="button"
                        disabled={isDownloadingAudit}
                      >
                        <FontAwesomeIcon icon={faDownload} />
                        <span className="pl-2">
                          {isDownloadingAudit ? "Downloading..." : "Audit CSV"}
                        </span>
                      </button>
                    </MenuItem>
                  </div>
                </MenuItems>
              </Menu>

              <FontAwesomeIcon
                icon={faPlus}
                size="sm"
                className="bg-[#5057FF] text-white p-2 rounded cursor-pointer"
                onClick={() => setIsPaymentModalOpen(true)}
              />
            </div>
          )}
        </div>

        {/* Search */}
        <AttendeesSearchList
          attendees={attendeesList}
          eventId={eventId}
          selectedAttendee={selectedAttendee}
          onSearch={handleSearch}
          onSelectAttendee={handleSelectAttendee}
          adjustedHeight={adjustedHeight}
        />
      </div>
      {open && (
        <AthleteContactModal
          attendees={attendees}
          eventName={eventName}
          onClose={() => setOpen(false)}
        />
      )}
      <AttendeesPaymentMethodDialog
        open={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onSelectPaymentMethod={handlePaymentMethodSelect}
      />
      <AttendeesQrCodeDialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        eventName={eventName}
        qrCodeUrl={qrCodeUrl}
      />
      <SendEmailModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        attendees={attendees || []}
        eventId={eventId}
        eventName={eventName}
      />
    </>
  );
}
